import React from 'react';
import { Award, Heart, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { drRupesh, clinicInfo } from '../data/mockData';

const AboutPage = () => {
  const handleWhatsApp = () => {
    window.open(`https://wa.me/${clinicInfo.whatsapp.replace(/[^0-9]/g, '')}?text=Hi, I would like to book an appointment`, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-secondary to-background py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
                About Mind Motion
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed mb-6">
                Founded with a mission to make evidence-based, personalized physiotherapy accessible to people in and around Bellandur, Mind Motion believes pain is not something you should have to live with. In today's fast-paced lifestyle, long hours, stress, and sedentary routines quietly build into persistent discomfort.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                We take a different approach. Instead of quick fixes, we focus on understanding you — your body, routine, and goals. Every treatment is tailored to your needs.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Because real recovery isn't just relief — it's moving better, feeling stronger, and living fully again.
              </p>
            </div>
            <div className="relative">
              <img 
                src="/images/wmm1.jpg" 
                alt="Mind Motion Clinic"
                className="rounded-lg shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Meet Dr. Rupesh */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Meet Dr. Rupesh Kumar
            </h2>
            <p className="text-lg text-muted-foreground">
              Founder & Lead Physiotherapist
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 md:order-1">
              <Card className="border-2 border-border">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-primary mb-4">Qualifications</h3>
                  <p className="text-muted-foreground mb-6">{drRupesh.qualification}</p>
                  
                  <h3 className="text-xl font-bold text-primary mb-4">Specialization</h3>
                  <p className="text-muted-foreground mb-6">{drRupesh.specialization}</p>
                  
                  <h3 className="text-xl font-bold text-primary mb-4">Experience</h3>
                  <p className="text-muted-foreground">{drRupesh.experience} in clinical practice</p>
                </CardContent>
              </Card>
            </div>
            <div className="order-1 md:order-2">
              <img 
                src={drRupesh.image} 
                alt="Dr. Rupesh Kumar"
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
          <div className="max-w-4xl mx-auto mt-12">
            <Card className="bg-secondary border-none">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-primary mb-4 text-center">
                  Philosophy of Care
                </h3>
                <p className="text-lg text-muted-foreground leading-relaxed italic text-center">
                  "{drRupesh.philosophy}"
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 md:py-24 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Our Core Values
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-border hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary rounded-full mb-6">
                  <Award className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-primary mb-3">Excellence</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Committed to the highest standards of clinical practice and continuous professional development
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-border hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary rounded-full mb-6">
                  <Heart className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-primary mb-3">Compassion</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Understanding that every patient's journey is unique and deserves empathy and personalized care
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-border hover:shadow-lg transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary rounded-full mb-6">
                  <Target className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-primary mb-3">Results</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Focused on measurable outcomes that help patients return to their active, pain-free lifestyles
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Experience Personalized Care
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Ready to start your journey to pain-free movement? Book your comprehensive assessment today.
          </p>
          <Button 
            onClick={handleWhatsApp}
            size="lg"
            className="text-white text-lg px-8 py-6 transition-all duration-300"
          >
            Book Appointment
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
